import { cva, type VariantProps } from 'class-variance-authority'
import { Popover as PopoverPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/src/designSystem/lib/utils'

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot='popover' {...props} />
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot='popover-trigger' {...props} />
}

const popoverContentVariants = cva(
  'z-50 flex origin-(--radix-popover-content-transform-origin) flex-col rounded-lg bg-popover text-xs text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
  {
    variants: {
      layout: {
        default: 'w-72 gap-4 p-2.5',
        form: 'w-[min(28rem,calc(100vw-1rem))] gap-4 p-4',
        /**
         * For content that owns its own layout, e.g. a Command list: no
         * popover padding/gap, and the narrower list width.
         */
        flush: 'w-68 gap-0 p-0',
        /** Flush, at full popover width — for wider command menus. */
        flushWide: 'w-80 gap-0 p-0',
        /**
         * Flush and sized by its content — for a single control, such as one
         * button, that fills the popover. Not clipped, so the control's focus
         * ring stays visible.
         */
        flushHug: 'w-auto gap-0 p-0',
        /**
         * Width from the content, with the trigger as the floor so a
         * narrow trigger never dangles off a wider popover.
         */
        hug: 'w-auto min-w-(--radix-popover-trigger-width) gap-2 p-2',
        /** Flush, exactly the trigger width — for full-width form filters. */
        matchTrigger:
          'w-(--radix-popover-trigger-width) min-w-(--radix-popover-trigger-width) gap-0 p-0',
      },
      fitViewport: {
        true: 'max-h-(--radix-popover-content-available-height) overflow-y-auto',
      },
    },
    defaultVariants: {
      layout: 'default',
    },
  }
)

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  layout,
  fitViewport,
  container,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> &
  VariantProps<typeof popoverContentVariants> &
  /**
   * Portal target. Defaults to `document.body`; pass an element inside an
   * open Dialog/Sheet so the dialog's scroll lock lets the popover scroll on
   * touch.
   */
  Pick<React.ComponentProps<typeof PopoverPrimitive.Portal>, 'container'>) {
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Content
        data-slot='popover-content'
        align={align}
        sideOffset={sideOffset}
        className={cn(
          popoverContentVariants({ layout, fitViewport }),
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot='popover-anchor' {...props} />
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='popover-header'
      className={cn('flex flex-col gap-1 text-xs', className)}
      {...props}
    />
  )
}

function PopoverTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <div
      data-slot='popover-title'
      className={cn('text-sm font-medium', className)}
      {...props}
    />
  )
}

function PopoverDescription({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot='popover-description'
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
}
